import { IonRouterLink } from '@ionic/react';
import { ComponentProps, ReactNode } from 'react';

import styles from '@components/buttons/ActionIconButton.module.css';
import ConditionalTooltip from '@components/ConditionalTooltip';
import ActionIcon from '@components/typography/icons/ActionIcon';
import useKeyboardFocus from '@hooks/useKeyboardFocus';
import { IconName } from '@utils/iconUtils';
import { clickCurrentTarget } from '@utils/keyboardUtils';

type LinkProps = Pick<
  ComponentProps<typeof IonRouterLink>,
  'href' | 'onClick' | 'rel' | 'routerLink' | 'target' | 'id'
>;

type Props = LinkProps & {
  active?: boolean;
  ariaLabelKey: string;
  color?: 'primary700' | 'shadesWhite' | 'neutral500' | 'neutral200' | 'success300' | 'secondary700' | 'error300';
  disabled?: boolean;
  hoverContent?: ReactNode;
  icon: IconName;
  size?: 'small' | 'medium' | 'large';
  skipSpacing?: boolean;
};

const ActionIconButton = ({
  active = false,
  ariaLabelKey,
  color = 'primary700',
  disabled = false,
  hoverContent,
  icon,
  size,
  ...linkProps
}: Props) => {
  const keyboardFocus = useKeyboardFocus();

  return (
    <div>
      <ConditionalTooltip condition={!!hoverContent} content={hoverContent}>
        {tooltipTriggerProps => {
          if (disabled) {
            return (
              <ActionIcon
                ariaLabelKey={ariaLabelKey}
                disabled
                icon={icon}
                size={size}
                tabIndex={tooltipTriggerProps && 0}
                {...tooltipTriggerProps}
              />
            );
          }

          return (
            <IonRouterLink
              className={`${styles.link} ${active ? styles.active : color === 'shadesWhite' ? styles.skipHover : ''}  ${
                keyboardFocus ? styles.focus : ''
              }`}
              data-test="action-icon-button"
              {...linkProps}
              onKeyUp={clickCurrentTarget}
              {...tooltipTriggerProps}
            >
              <ActionIcon ariaLabelKey={ariaLabelKey} color={color} icon={icon} size={size} />
            </IonRouterLink>
          );
        }}
      </ConditionalTooltip>
    </div>
  );
};

export default ActionIconButton;
